import {
  Card,
  Group,
  Text,
  Badge,
  ActionIcon,
  Menu,
  Stack,
  Tooltip,
} from '@mantine/core';
import {
  IconDots,
  IconToggleLeft,
  IconToggleRight,
  IconTrash,
  IconWorld,
} from '@tabler/icons-react';
import { useNavigate } from '@tanstack/react-router';
import { formatRelative } from '@/shared/utils/date';
import type { AppCardData, AppCfgsDto } from '@/lib/types/App';

interface AppCardProps {
  card: AppCardData;
  appDto: AppCfgsDto;
  onToggle: (appCfgId: number, currentApp: AppCfgsDto) => void;
  onDelete: (appCfgId: number, name: string) => void;
}

export function AppCard({ card, appDto, onToggle, onDelete }: AppCardProps) {
  const navigate = useNavigate();

  return (
    <Card
      shadow="xs"
      padding="lg"
      radius="md"
      withBorder
      style={{ cursor: 'pointer' }}
      onClick={() => navigate({ to: '/apps/$id', params: { id: card.displayId } })}
    >
      <Group justify="space-between" mb="xs">
        <Text fw={600} size="md" lineClamp={1} style={{ flex: 1 }}>
          {card.name}
        </Text>
        <Menu shadow="md" width={160} position="bottom-end" withinPortal>
          <Menu.Target>
            <ActionIcon
              variant="subtle"
              color="gray"
              size="sm"
              onClick={(e) => e.stopPropagation()}
            >
              <IconDots size={16} />
            </ActionIcon>
          </Menu.Target>
          <Menu.Dropdown onClick={(e) => e.stopPropagation()}>
            <Menu.Item
              leftSection={
                card.isActive ? <IconToggleLeft size={14} /> : <IconToggleRight size={14} />
              }
              onClick={() => onToggle(card.appCfgId, appDto)}
            >
              {card.isActive ? 'Deactivate' : 'Activate'}
            </Menu.Item>
            <Menu.Item
              color="red"
              leftSection={<IconTrash size={14} />}
              onClick={() => onDelete(card.appCfgId, card.name)}
            >
              Delete
            </Menu.Item>
          </Menu.Dropdown>
        </Menu>
      </Group>

      <Stack gap={6}>
        <Group gap="xs">
          <Badge
            size="sm"
            color={card.isActive ? 'green' : 'gray'}
            variant="light"
          >
            {card.isActive ? 'Active' : 'Inactive'}
          </Badge>
          <Text size="xs" c="dimmed" ff="monospace">
            {card.displayId}
          </Text>
        </Group>

        <Text size="sm" c="dimmed" lineClamp={1}>
          {card.contentGroupTitle}
        </Text>

        {card.hasWeb && (
          <Group gap="xs">
            <Tooltip label="Web app enabled">
              <Badge
                size="xs"
                variant="light"
                color="blue"
                leftSection={<IconWorld size={10} />}
              >
                Web
              </Badge>
            </Tooltip>
          </Group>
        )}
        {/* Roku badge hidden — not ready for customer support yet
        {card.hasRoku && (
          <Tooltip label="Roku app enabled">
            <Badge size="xs" variant="light" color="violet" leftSection={<IconDeviceTv size={10} />}>
              Roku
            </Badge>
          </Tooltip>
        )}
        */}

        {card.webDomain && (
          <Text size="xs" c="blue.6" lineClamp={1}>
            {card.webDomain}
          </Text>
        )}

        <Text size="xs" c="dimmed">
          Created {formatRelative(card.created)}
        </Text>
      </Stack>
    </Card>
  );
}
